import type { CapabilityDefect } from '../../../shared/services/vendor-capability-defects';
import type {
  Channel,
  ResolvedVendor,
} from '../../../shared/services/vendor-channel-resolver.service';
import { type Observation, referenceAsPrice, shownReference } from './observation';
import type { PriceFieldConvention } from './vendor-price-conventions';

// Prod sample: 1.8-3.7 percent of priced days above list per online vendor (up to 9.2 at brand grain) under the declared mapping, 22-38 under the wrong one.
export const CONTRADICTED_SHARE_THRESHOLD = 0.15;

// Below this many priced days per (vendor, channel) a share is too noisy to contradict a convention.
const MIN_PRICED_DAYS = 200;

// The shares above were measured with this 3 percent margin, so the threshold holds only with it.
const ABOVE_LIST_TOLERANCE = 1.03;

type PricedTally = { priced: number; aboveList: number };

const isPositive = (value: number | null): value is number =>
  value !== null && Number.isFinite(value) && value > 0;

/**
 * One pass over a decoded batch: each distinct (vendor, channel) it observed, aggregate
 * included, and the price-convention defects its priced days show.
 */
export function scanVendorChannels(
  observations: Iterable<Observation>,
  conventionOf: (vendor: string) => PriceFieldConvention | null
): { readonly seen: readonly ResolvedVendor[]; readonly defects: readonly CapabilityDefect[] } {
  const tallies = new Map<string, Map<Channel, PricedTally>>();
  const seen: ResolvedVendor[] = [];

  for (const { vendor, channel, observed } of observations) {
    let byChannel = tallies.get(vendor);
    if (!byChannel) {
      byChannel = new Map();
      tallies.set(vendor, byChannel);
    }
    let tally = byChannel.get(channel);
    if (!tally) {
      tally = { priced: 0, aboveList: 0 };
      byChannel.set(channel, tally);
      seen.push({ vendor, channel });
    }

    if (channel === 'aggregate') continue;
    const { price } = observed;
    const reference = shownReference(observed);
    if (!isPositive(price) || reference === null) continue;
    tally.priced++;
    if (price > referenceAsPrice(reference) * ABOVE_LIST_TOLERANCE) tally.aboveList++;
  }

  const defects: CapabilityDefect[] = [];
  for (const [vendor, byChannel] of tallies) {
    for (const [channel, { priced, aboveList }] of byChannel) {
      if (priced === 0) continue;
      if (conventionOf(vendor) === null) {
        defects.push({ kind: 'undeclared-price-convention', vendor, channel });
      }
      if (
        channel === 'online' &&
        priced >= MIN_PRICED_DAYS &&
        aboveList > priced * CONTRADICTED_SHARE_THRESHOLD
      ) {
        defects.push({ kind: 'price-convention-contradicted', vendor, channel });
      }
    }
  }
  return { seen, defects };
}

/**
 * The vendors in a decoded batch whose price-field convention is undeclared, or contradicted
 * by the batch's own priced days, one defect per (kind, vendor, channel).
 */
export function findPriceConventionDefects(
  observations: Iterable<Observation>,
  conventionOf: (vendor: string) => PriceFieldConvention | null
): readonly CapabilityDefect[] {
  return scanVendorChannels(observations, conventionOf).defects;
}
